import React from 'react';
import { Link } from 'react-router-dom';
import { ActionMenu, ActionMenuItem } from './ActionMenu';

export interface ResponsiveEntityColumn<T> {
  header: string;
  cell: (item: T) => React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

export interface ResponsiveEntityField {
  label: string;
  value: React.ReactNode;
}

interface ResponsiveEntityListProps<T> {
  items: T[];
  getKey: (item: T) => string;
  columns: ResponsiveEntityColumn<T>[];
  actions: (item: T) => ActionMenuItem[];
  mobileTitle: (item: T) => React.ReactNode;
  mobileFields: (item: T) => ResponsiveEntityField[];
  mobileTo?: (item: T) => string | undefined;
  embedded?: boolean;
}

export function ResponsiveEntityList<T>({
  items,
  getKey,
  columns,
  actions,
  mobileTitle,
  mobileFields,
  mobileTo,
  embedded = false,
}: ResponsiveEntityListProps<T>) {
  return (
    <>
      <ul className={`md:hidden ${embedded ? 'space-y-2' : 'space-y-3'}`}>
        {items.map((item) => {
          const href = mobileTo?.(item);
          const title = (
            <div className="text-sm font-semibold text-gray-900">{mobileTitle(item)}</div>
          );

          return (
            <li
              key={getKey(item)}
              className={embedded ? 'rounded-lg border border-gray-200 bg-white p-3' : 'bg-white rounded-lg shadow p-4'}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  {href ? (
                    <Link to={href} className="block hover:text-blue-700">
                      {title}
                    </Link>
                  ) : (
                    title
                  )}
                  <dl className="mt-2 space-y-1">
                    {mobileFields(item).map((field) => (
                      <div key={field.label} className="flex gap-2 text-sm">
                        <dt className="flex-shrink-0 text-gray-500">{field.label}</dt>
                        <dd className="min-w-0 text-gray-800">{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="flex-shrink-0 -mt-1 -mr-1">
                  <ActionMenu items={actions(item)} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className={embedded ? 'hidden md:block overflow-hidden' : 'hidden md:block bg-white rounded-lg shadow overflow-hidden'}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.header}
                  className={column.headerClassName ?? 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase'}
                >
                  {column.header}
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={getKey(item)} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td
                    key={column.header}
                    className={column.cellClassName ?? 'px-6 py-4 text-sm text-gray-600'}
                  >
                    {column.cell(item)}
                  </td>
                ))}
                <td className="px-6 py-4">
                  <div className="flex justify-end">
                    <ActionMenu items={actions(item)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
